import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { css } from "@styled-system/css";
import { FormikProps } from "formik";
import React, { useCallback } from "react";

import {
  Alert,
  Box,
  Button,
  EntryList,
  FieldError,
  Flex,
  InputBirthdate,
  Radio,
  RadioGroup,
  Text,
  TextField,
} from "~/components/blocks";
import { numberOrHyphenRegexp } from "~/constants/regexp";
import { toHalfNumberWithHyphen } from "~/utils/convert";

import { Fields } from "../types";
import { useResetErrors } from "../use-reset-errors";

type Props = {
  firstAccess: boolean;
  formik: FormikProps<Fields>;
  onNext: () => void;
};

const Wrap = styled(Box)(({ theme }) =>
  css({
    width: "100%",
    maxWidth: theme.windowSize.base,
    margin: "0 auto",
    padding: "0",
    boxSizing: "border-box",
    [`@media ${theme.mediaQueries.sp}`]: {
      margin: `0 auto`,
    },
  }),
);

export const InputPatientInfo = React.memo((props: Props) => {
  const { formik } = props;
  const theme = useTheme();

  const hasError =
    !!formik.errors.familyName ||
    !!formik.errors.givenName ||
    !!formik.errors.phoneticFamilyName ||
    !!formik.errors.phoneticGivenName ||
    !!formik.errors.birthDate ||
    !!formik.errors.mobilePhone;

  const isInputted = !!(
    formik.values.familyName &&
    formik.values.givenName &&
    formik.values.phoneticFamilyName &&
    formik.values.phoneticGivenName &&
    formik.values.birthDate &&
    formik.values.mobilePhone
  );

  const handleChangeDate = useCallback(
    (date: string) => formik.setFieldValue("birthDate", date),
    [formik],
  );

  useResetErrors(formik, props.firstAccess);

  return (
    <Wrap>
      <Text block size="m" fontWeight="bold">
        患者情報を入力してください
      </Text>
      <Box marginTop={theme.space.m}>
        <Flex alignItems="flex-end">
          <EntryList width="100%">
            <EntryList.Head>姓</EntryList.Head>
            <EntryList.Body>
              <TextField
                name="familyName"
                placeholder="例：患者"
                value={formik.values.familyName}
                error={!!formik.errors.familyName}
                onChange={formik.handleChange}
              />
            </EntryList.Body>
          </EntryList>
          <EntryList marginLeft={theme.space.m} width="100%">
            <EntryList.Head>名</EntryList.Head>
            <EntryList.Body>
              <TextField
                name="givenName"
                placeholder="例：太郎"
                value={formik.values.givenName}
                error={!!formik.errors.givenName}
                onChange={formik.handleChange}
              />
            </EntryList.Body>
          </EntryList>
        </Flex>
        {formik.errors.familyName && (
          <FieldError error={formik.errors.familyName} />
        )}
        {formik.errors.givenName && (
          <FieldError error={formik.errors.givenName} />
        )}
        <Flex alignItems="flex-end" marginTop={theme.space.m}>
          <EntryList width="100%">
            <EntryList.Head>姓（カナ）</EntryList.Head>
            <EntryList.Body>
              <TextField
                name="phoneticFamilyName"
                placeholder="例：カンジャ"
                value={formik.values.phoneticFamilyName}
                error={!!formik.errors.phoneticFamilyName}
                onChange={formik.handleChange}
              />
            </EntryList.Body>
          </EntryList>
          <EntryList marginLeft={theme.space.m} width="100%">
            <EntryList.Head>名（カナ）</EntryList.Head>
            <EntryList.Body>
              <TextField
                name="phoneticGivenName"
                placeholder="例：タロウ"
                value={formik.values.phoneticGivenName}
                error={!!formik.errors.phoneticGivenName}
                onChange={formik.handleChange}
              />
            </EntryList.Body>
          </EntryList>
        </Flex>
        {formik.errors.phoneticFamilyName && (
          <FieldError error={formik.errors.phoneticFamilyName} />
        )}
        {formik.errors.phoneticGivenName && (
          <FieldError error={formik.errors.phoneticGivenName} />
        )}
        <EntryList marginTop={theme.space.m}>
          <EntryList.Head>生年月日</EntryList.Head>
          <EntryList.Body>
            <InputBirthdate
              error={formik.errors.birthDate}
              value={formik.values.birthDate || ""}
              onChange={handleChangeDate}
            />
          </EntryList.Body>
        </EntryList>
        <EntryList marginTop={theme.space.m}>
          <EntryList.Head>性別</EntryList.Head>
          <EntryList.Body>
            <RadioGroup>
              <Radio
                name="sex"
                label="男性"
                value="male"
                checked={formik.values.sex === "male"}
                onChange={formik.handleChange}
              />
              <Radio
                name="sex"
                label="女性"
                value="female"
                checked={formik.values.sex === "female"}
                onChange={formik.handleChange}
              />
            </RadioGroup>
          </EntryList.Body>
        </EntryList>
        <EntryList marginTop={theme.space.m}>
          <EntryList.Head>携帯電話番号</EntryList.Head>
          <EntryList.Body>
            <TextField
              inputMode="numeric"
              name="mobilePhone"
              placeholder="例：08012341234"
              value={formik.values.mobilePhone}
              error={formik.errors.mobilePhone}
              onBlur={(e) => {
                if (!numberOrHyphenRegexp.test(e.currentTarget.value)) return;
                formik.setFieldValue(
                  "mobilePhone",
                  toHalfNumberWithHyphen(e.currentTarget.value),
                );
              }}
              onChange={formik.handleChange}
            />
          </EntryList.Body>
        </EntryList>
        <Alert
          status="warning"
          justifyContent="center"
          marginTop={theme.space.xl}
        >
          <Text>
            電話番号宛（SMS）にお知らせするため
            <br />
            正確にご入力ください
          </Text>
        </Alert>
        <Button
          use="base"
          wide="fill"
          marginTop={theme.space.xl}
          disabled={hasError || !isInputted}
          onClick={props.onNext}
        >
          最後に問診にお答えください
        </Button>
      </Box>
    </Wrap>
  );
});

InputPatientInfo.displayName = "InputPatientInfo";
